import { useState, useEffect } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import ReviewForm from "@/components/ReviewForm";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then(({ data }) => setReviews(data))
      .catch(() => {});
  }, []);

  const all = reviews.map((r) => ({
    quote: r.text,
    name: r.name,
    event: r.event_type || "Dream Event",
    rating: r.rating,
    key: r.id,
  }));

  return (
    <main data-testid="testimonials-page">
      <PageHeader
        overline="Kind Words"
        title="Stories from"
        accent="the dance floor."
        description="We measure success in sore feet, hoarse voices, and guests who refuse to leave. Here's what our families say."
      />

      {/* LEAVE A REVIEW */}
      <section id="leave-review" className="max-w-3xl mx-auto px-6 sm:px-12 pb-20">
        <Reveal>
          <ReviewForm onSubmitted={(r) => setReviews([r, ...reviews])} />
        </Reveal>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-16">
        {all.length === 0 ? (
          <Reveal className="text-center py-16">
            <p
              data-testid="reviews-empty-state"
              className="font-serif text-3xl sm:text-4xl tracking-tight text-muted-foreground"
            >
              Real stories are on their way.{" "}
              <em className="text-primary">yours could be the first.</em>
            </p>
          </Reveal>
        ) : (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {all.map((t, i) => (
            <Reveal key={t.key} delay={(i % 3) * 0.1} className="break-inside-avoid">
              <figure
                data-testid={`testimonial-card-${i}`}
                className="glass rounded-3xl p-8 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_0_50px_rgba(255,0,127,0.1)]"
              >
                <span className="font-serif text-7xl leading-none text-primary/40 select-none">
                  &ldquo;
                </span>
                <blockquote className="font-serif text-xl sm:text-2xl leading-snug -mt-4">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-8 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium tracking-wide">{t.name}</p>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-1">
                      {t.event}
                    </p>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, s) => (
                      <Star
                        key={s}
                        size={12}
                        className={
                          s < t.rating
                            ? "fill-primary text-primary"
                            : "text-muted-foreground/30"
                        }
                      />
                    ))}
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
          </div>
        )}

        <Reveal className="text-center mt-20">
          <p className="font-serif text-3xl sm:text-4xl tracking-tight">
            Ready to add <em className="text-primary">your story?</em>
          </p>
          <Link
            to="/booking"
            data-testid="testimonials-cta-btn"
            className="mt-8 inline-block rounded-full bg-primary text-primary-foreground px-10 py-4 text-sm font-medium tracking-widest uppercase transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(255,0,127,0.4)]"
          >
            Book Your Date
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
